<template>
  <button
    :type="type"
    :variant="variant"
    :class="classes"
    :disabled="disabled"
    :loading="loading"
    :style="stylesObject"
    v-bind="$attrs"
    v-on="$listeners"
  >
    <span
      v-if="loading"
      class="button__loading"
    >
      <font-awesome-icon
        icon="spinner"
        spin
      />
    </span>

    <span class="button__content">

      <span
        v-if="icon"
        class="button__icon"
      >
        <font-awesome-icon
          :icon="icon"
          fixed-width
        />
      </span>

      <span
        v-if="containsContent"
        class="button__text"
      >
        <slot />
      </span>
    </span>
  </button>
</template>

<script>
const SUPPORTED_VARIANTS = ['primary', 'secondary', 'success', 'text'];
const SUPPORTED_SIZES = ['xs', 'sm', 'md', 'lg', 'xl', 'xxl'];

export default {
  name: 'TgButton',

  inheritAttrs: false,

  props: {
    type: {
      type: String,
      default: 'submit',
    },

    variant: {
      type: String,
      default: 'primary',
    },

    size: {
      type: String,
      default: 'md',
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    icon: {
      type: String,
      required: false,
      default: null,
    },

    loading: {
      type: Boolean,
      default: false,
    },

    stylesObject: {
      type: Object,
      required: false,
      default () {
        return {};
      },
    },
  },

  computed: {
    classes () {
      const classes = ['button'];

      if (this.variant && SUPPORTED_VARIANTS.includes(this.variant)) {
        classes.push(`button--${this.variant}`);
      }

      if (this.icon && this.containsContent === false) {
        classes.push('button--icon');
      }

      if (this.loading) {
        classes.push('button--loading');
      }

      if (this.size && SUPPORTED_SIZES.includes(this.size)) {
        classes.push(`button--${this.size}`);
      }

      return classes;
    },

    containsContent () {
      return typeof this.$slots.default !== 'undefined';
    },
  },
};
</script>

<style scoped>
  .button {
    position: relative;
    overflow: visible;
    width: 100%;
    height: 2.8125rem;
    font-size: 1rem;
    font-weight: 400;
    margin: 0;
    padding: 0;
    cursor: pointer;
    transition: all .3s ease 0s;
    border: 0;
    background-color: var(--primary);
    border-radius: 2rem;
    color: #fff;
  }

  .button__content {
    padding-inline: 1.5em;
  }

  .button,
  .button:link,
  .button:visited {
    transition: all var(--transition-out);
  }

  .button:focus:not(:disabled),
  .button:active:not(:disabled),
  .button:hover:not(:disabled) {
    outline: 0;
    box-shadow: 0 0 0 62.5rem rgba(0, 0, 0, .075) inset;
    transition: all var(--transition-in);
  }

  .button--secondary,
  .button--secondary:link,
  .button--secondary:visited,
  .button:not(.button--text):disabled {
    background-color: var(--light-grey);
    color: var(--body-color);
  }

  .button--success,
  .button--success:link,
  .button--success:visited {
    background-color: var(--success);
  }

  .button--text,
  .button--text:link,
  .button--text:visited {
    background-color: transparent;
    color: var(--body-color);
    text-decoration: none;
  }

  .button--text:hover,
  .button--text:active,
  .button--text:focus {
    color: var(--primary);
  }

  .button--icon {
    width: 2.8125rem;
    padding: 0;
    overflow: hidden;
  }

  .button:disabled {
    color: rgb(55 55 55 / 50%) !important;
    cursor: not-allowed !important;
  }

  .button__loading,
  .button--icon .button__icon {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
  }

  .button__loading + .button__content {
    opacity: 0;
  }

  .button__loading {
    pointer-events: none;
  }

  .button__icon {
    position: absolute;
    top: 50%;
    left: 1.25em;
    transform: translate(-50%, -50%);
  }

  .button .button__icon + .button__text {
    padding-left: .75em;
  }

  .button--xl {
    font-size: 1.25rem;
    height: 3.75rem;
  }

  .button--xl .button__icon + .button__text {
    padding-left: 1em;
  }

  .button--icon.button--xl {
    width: 3.75rem;
  }

  .button--xl .button__icon {
    font-size: 1.5rem;
  }

  .button--sm {
    font-size: .875rem;
    height: 2.25rem;
  }

  @media all and (min-width: 570px) {
    .button:not(.button--icon) {
      width: initial;
    }
  }
</style>
